import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './map.css';
import { logMapViews } from '../../api/rpc';
import type { GameState, StateSpot } from '../../api/types';
import { config } from '../../config';
import { fmtRemaining, remainingMs, serverNow } from '../../core/clock';
import { deckle } from '../../core/deckle';
import { geo } from '../../core/geo';
import { openView } from '../../core/viewlog';
import type { Notice, Tab, TabContext, TabFactory, TabShowOptions } from '../contract';
import { computeAreaContours, DEFAULT_BLEND_M, DEFAULT_REACH_M, type AreaSites, type LatLon } from './areas';
import {
  areaColor,
  buildStrip,
  duelOf,
  duelRule,
  GpsSlipSchedule,
  gpsAgoText,
  isGpsLost,
  MapViewBatcher,
  queensOf,
  scoutCircles,
  spotButton,
  spotNumber,
  formatMult,
  type QueenView,
  type StripItem,
} from './logic';

const FALLBACK_CENTER: LatLon = [35.9172, 139.9098];
const FOLD_MS = 550;
const FOLD_ANGLE = 80;
const SVG_NS = 'http://www.w3.org/2000/svg';
const INK = '#2E2116';
const HONEY = '#E0B23A';

type Child = Node | string | null | undefined | false;

/** 要素を作る。文字は textContent（サーバー由来の文字列を HTML として解釈しない） */
function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, ...kids: Child[]): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  for (const k of kids) {
    if (k === null || k === undefined || k === false) continue;
    e.append(typeof k === 'string' ? document.createTextNode(k) : k);
  }
  return e;
}

/** 固定の SVG 文字列から要素を作る（innerHTML を使わない） */
function svgFrom(src: string): SVGElement {
  const doc = new DOMParser().parseFromString(src, 'image/svg+xml');
  return document.importNode(doc.documentElement, true) as unknown as SVGElement;
}

/** ×1.2 の形（×は小さく） */
function multEl(text: string, cls = 'mp-x'): HTMLElement {
  return h('span', cls, h('small', undefined, '×'), text);
}

let uid = 0;

/** ⑱ 現在地の蜂（上から見た形。頭と触角2本・羽・縞2本の腹・針） */
function beeSvg(): SVGElement {
  const id = `mp-bb-${++uid}`;
  return svgFrom(
    `<svg xmlns="${SVG_NS}" class="mp-bee" viewBox="0 0 32 32" aria-hidden="true"><defs><clipPath id="${id}"><path d="M10.5 15.2Q10.5 11.4 16 11.4Q21.5 11.4 21.5 15.2Q21.5 25.4 16 26.4Q10.5 25.4 10.5 15.2Z"/></clipPath></defs>` +
      `<ellipse cx="8.2" cy="11.6" rx="6.4" ry="3.7" transform="rotate(-18 8.2 11.6)" class="w"/><ellipse cx="23.8" cy="11.6" rx="6.4" ry="3.7" transform="rotate(18 23.8 11.6)" class="w"/>` +
      `<path d="M15 25.4L16 28.8L17 25.4Z" class="h"/><path d="M10.5 15.2Q10.5 11.4 16 11.4Q21.5 11.4 21.5 15.2Q21.5 25.4 16 26.4Q10.5 25.4 10.5 15.2Z" class="b"/>` +
      `<g clip-path="url(#${id})"><rect x="8" y="16.2" width="16" height="2.5" class="h"/><rect x="8" y="21" width="16" height="2.5" class="h"/></g>` +
      `<path d="M10.5 15.2Q10.5 11.4 16 11.4Q21.5 11.4 21.5 15.2Q21.5 25.4 16 26.4Q10.5 25.4 10.5 15.2Z" class="o"/>` +
      `<path d="M14.8 5.6Q14 2.8 11.8 2.2M17.2 5.6Q18 2.8 20.2 2.2" class="a"/><circle cx="16" cy="8.2" r="3.4" class="h"/></svg>`,
  );
}

/** ⑲c 女王蜂（長い濃い橙の腹に縞3本・短い羽・針・紫の王冠） */
function queenSvg(): SVGElement {
  const id = `mp-qb-${++uid}`;
  const body = 'M11 17.6Q11 13.8 16 13.8Q21 13.8 21 17.6Q21 31.6 16 33Q11 31.6 11 17.6Z';
  const svg = svgFrom(
    `<svg xmlns="${SVG_NS}" class="mp-bee mp-qb" viewBox="0 0 32 40" aria-hidden="true"><defs><clipPath id="${id}"><path d="${body}"/></clipPath></defs>` +
      `<ellipse cx="9.6" cy="15.4" rx="5.4" ry="3" transform="rotate(-20 9.6 15.4)" class="w"/><ellipse cx="22.4" cy="15.4" rx="5.4" ry="3" transform="rotate(20 22.4 15.4)" class="w"/>` +
      `<path d="M15.1 32.2L16 35.6L16.9 32.2Z" class="h"/><path d="${body}" class="qb"/>` +
      `<g clip-path="url(#${id})"><rect x="9" y="19.4" width="14" height="2.3" class="h"/><rect x="9" y="23.8" width="14" height="2.3" class="h"/><rect x="9" y="28.2" width="14" height="2.3" class="h"/></g>` +
      `<path d="${body}" class="o"/><path d="M13.3 11.6Q11.2 10.4 9 10.6M18.7 11.6Q20.8 10.4 23 10.6" class="a"/><circle cx="16" cy="11.4" r="3.1" class="h"/>` +
      `<path d="M10.9 10.4L10.6 3.6L13.4 6.4L16 2L18.6 6.4L21.4 3.6L21.1 10.4Z" class="cr"/></svg>`,
  );
  return svg;
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

export const createMapTab: TabFactory = (ctx: TabContext): Tab => {
  // ------------------------------------------------------------ DOM
  const el = h('div', 'mp-root');
  const sheet = h('div', 'mp-sheet');
  const face = h('div', 'mp-face');
  const mapDiv = h('div', 'mp-map');
  face.append(mapDiv);
  sheet.append(face);
  const stripEl = h('div', 'mp-strip');
  stripEl.setAttribute('role', 'group');
  stripEl.setAttribute('aria-label', 'エリアの倍率');
  const duelEl = h('div', 'mp-duel');
  duelEl.hidden = true;
  duelEl.setAttribute('role', 'status');
  duelEl.setAttribute('aria-label', '縄張り争い');
  const head = h('div', 'mp-head', stripEl, duelEl);
  // シェルが荷札の高さを決めるのに使う。倍率の帯と縄張り争いの帯を1つの塊にして、その下端まで隠す
  head.setAttribute('data-notice-floor', '');
  const slipTitle = h('b', undefined, 'GPSが取得できていません');
  const slipAgo = h('b', 'mp-num', '');
  const slip = h('div', 'mp-slip', slipTitle, h('span', undefined, '最終取得時間：', slipAgo));
  slip.setAttribute('role', 'status');
  const detail = h('div', 'mp-detail');
  detail.hidden = true;
  const spotEl = h('div', 'mp-spot');
  spotEl.setAttribute('role', 'dialog');
  spotEl.setAttribute('aria-label', 'スポットの詳細');
  el.append(sheet, head, slip, detail, spotEl);

  // ------------------------------------------------------------ 状態
  let state: GameState | null = null;
  let map: L.Map | null = null;
  let tiles: L.TileLayer | null = null;
  let shown = false;
  let folding = false;
  let fitted = false;
  let hadFirstShow = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  let stripSig = '';
  let areasSig = '';
  let queensSig = '';
  let scoutsSig = '';
  let openAreaId: string | null = null;
  let selectedSpot: number | null = null;
  let spotView: Promise<(reason?: string) => void> | null = null;
  let pendingFocus: TabShowOptions['focus'] | null = null;
  let scoutFocusPending = false;
  const slipSchedule = new GpsSlipSchedule();
  const hiddenLabels = new Set<string>();
  const contourCache: { sig: string; rings: Map<string, LatLon[][]> } = { sig: '', rings: new Map() };
  const batcher = new MapViewBatcher({
    send: (views) => logMapViews(views),
  });

  // レイヤー
  const areaLayer = L.layerGroup();
  const pinLayer = L.layerGroup();
  const queenLayer = L.layerGroup();
  const scoutLayer = L.layerGroup();
  const pins = new Map<number, { marker: L.Marker; sig: string }>();
  const queens = new Map<string, { marker: L.Marker; root: HTMLElement; label: HTMLElement | null; ago: HTMLElement | null; at: string | null }>();
  let meMarker: L.Marker | null = null;
  let meCircle: L.Circle | null = null;

  // ------------------------------------------------------------ 地図の用意（最初に見えたときに作る）
  function ensureMap(): L.Map {
    if (map) return map;
    const m = L.map(mapDiv, {
      zoomControl: false,
      attributionControl: false,
      zoomSnap: 1,
      minZoom: 3,
      maxZoom: 19,
      center: FALLBACK_CENTER,
      zoom: 16,
    });
    tiles = L.tileLayer(config.tileUrl, { maxZoom: 19, attribution: '© OpenStreetMap contributors' }).addTo(m);
    L.control.attribution({ prefix: false, position: 'bottomright' }).addTo(m);
    areaLayer.addTo(m);
    scoutLayer.addTo(m);
    pinLayer.addTo(m);
    queenLayer.addTo(m);
    m.on('moveend', onMoveEnd);
    m.on('click', () => {
      closeDetail();
      closeSpot('close');
    });
    m.on('dragstart', () => closeSpot('close'));
    // 地図に触れたら倍率の詳細は閉じる（印を押す前の touch も含める）
    mapDiv.addEventListener('pointerdown', closeDetail, { capture: true });
    map = m;
    return m;
  }

  function onMoveEnd(): void {
    if (!map || !shown || folding) return;
    const b = map.getBounds();
    batcher.push({ client_at: new Date().toISOString(), n: b.getNorth(), s: b.getSouth(), e: b.getEast(), w: b.getWest(), zoom: map.getZoom() });
  }

  // ------------------------------------------------------------ 描画（地図のレイヤー）
  function areaSites(s: GameState): AreaSites[] {
    return s.areas.map((a) => ({
      areaId: a.area_id,
      sites: [
        ...s.spots.filter((p) => p.area_id === a.area_id).map((p) => ({ lat: p.latitude, lon: p.longitude })),
        ...s.prompts
          .filter((p) => p.area_id === a.area_id && p.fixed_location && p.latitude !== null && p.longitude !== null)
          .map((p) => ({ lat: p.latitude as number, lon: p.longitude as number })),
      ],
    }));
  }

  /** サーバーの area_blend_m / area_reach_m が state に入っていればそれを使う（無ければ既定 20 / 80） */
  function areaParams(s: GameState): { blendM: number; reachM: number } {
    const ev = s.event as unknown as { area_blend_m?: unknown; area_reach_m?: unknown };
    return {
      blendM: typeof ev.area_blend_m === 'number' ? ev.area_blend_m : DEFAULT_BLEND_M,
      reachM: typeof ev.area_reach_m === 'number' ? ev.area_reach_m : DEFAULT_REACH_M,
    };
  }

  function renderAreas(s: GameState, items: StripItem[]): void {
    const sites = areaSites(s);
    const { blendM, reachM } = areaParams(s);
    const geomSig = JSON.stringify([blendM, reachM, sites.map((a) => [a.areaId, a.sites.map((p) => [Math.round(p.lat * 1e6), Math.round(p.lon * 1e6)])])]);
    const sig = geomSig + JSON.stringify(items.map((i) => [i.areaId, i.color, !!i.honey, !!i.hornet]));
    if (sig === areasSig) return;
    areasSig = sig;
    if (contourCache.sig !== geomSig) {
      contourCache.rings = computeAreaContours(sites, { blendM, reachM });
      contourCache.sig = geomSig;
    }
    areaLayer.clearLayers();
    for (const it of items) {
      const rings = contourCache.rings.get(it.areaId);
      if (!rings?.length) continue;
      // 蜜源の塗り・襲来の塗り（縁は付けない）→ 縁の実線 2.5px（塗りは入れない）
      if (it.honey) L.polygon(rings, { stroke: false, fill: true, fillColor: HONEY, fillOpacity: 0.28, interactive: false }).addTo(areaLayer);
      if (it.hornet) L.polygon(rings, { stroke: false, fill: true, fillColor: INK, fillOpacity: 0.24, interactive: false }).addTo(areaLayer);
      L.polygon(rings, { color: it.color, weight: 2.5, opacity: 1, fill: false, lineJoin: 'round', className: 'mp-area', interactive: false }).addTo(areaLayer);
    }
  }

  function pinIcon(color: string, cls: string): L.DivIcon {
    const i = h('i');
    i.style.setProperty('--c', color);
    return L.divIcon({ html: i, className: `mp-pin ${cls}`.trim(), iconSize: [44, 44], iconAnchor: [22, 22] });
  }

  function renderPins(s: GameState, items: StripItem[]): void {
    const byArea = new Map(items.map((i) => [i.areaId, i]));
    const seen = new Set<number>();
    s.spots.forEach((sp, idx) => {
      seen.add(sp.spot_id);
      const it = byArea.get(sp.area_id);
      const color = it?.color ?? areaColor(0, null);
      const cls = [sp.submitted ? 'mp-done' : '', !sp.submitted && it?.hornet ? 'mp-blk' : '', selectedSpot === sp.spot_id ? 'mp-sel' : ''].filter(Boolean).join(' ');
      const sig = `${color}|${cls}|${sp.latitude}|${sp.longitude}`;
      const cur = pins.get(sp.spot_id);
      const label = `スポット${idx + 1} ${sp.name}${sp.submitted ? ' クリア済み' : ''}`;
      if (!cur) {
        const marker = L.marker([sp.latitude, sp.longitude], { icon: pinIcon(color, cls), title: label, alt: label, keyboard: true, zIndexOffset: sp.submitted ? 0 : 100 });
        marker.on('click', (ev) => {
          L.DomEvent.stopPropagation(ev);
          openSpot(sp.spot_id);
        });
        marker.addTo(pinLayer);
        pins.set(sp.spot_id, { marker, sig });
      } else if (cur.sig !== sig) {
        cur.marker.setLatLng([sp.latitude, sp.longitude]);
        cur.marker.setIcon(pinIcon(color, cls));
        cur.marker.setZIndexOffset(sp.submitted ? 0 : 100);
        cur.sig = sig;
      }
      const e = pins.get(sp.spot_id)?.marker.getElement();
      if (e) {
        e.setAttribute('role', 'button');
        e.setAttribute('aria-label', label);
      }
    });
    for (const [id, p] of pins) {
      if (!seen.has(id)) {
        pinLayer.removeLayer(p.marker);
        pins.delete(id);
      }
    }
  }

  function renderMe(): void {
    if (!map) return;
    const fix = geo.lastFix;
    if (!fix) {
      meMarker?.remove();
      meCircle?.remove();
      meMarker = null;
      meCircle = null;
      return;
    }
    const lost = isGpsLost(geo.status);
    const ll: L.LatLngTuple = [fix.lat, fix.lon];
    if (!meMarker) {
      const wrap = h('div', 'mp-me');
      wrap.append(beeSvg());
      meMarker = L.marker(ll, {
        icon: L.divIcon({ html: wrap, className: 'mp-me-icon', iconSize: [44, 44], iconAnchor: [22, 22] }),
        interactive: false,
        keyboard: false,
        zIndexOffset: 1000,
      }).addTo(map);
    } else meMarker.setLatLng(ll);
    meMarker.getElement()?.querySelector('.mp-me')?.classList.toggle('mp-lost', lost);
    meMarker.getElement()?.setAttribute('aria-label', lost ? '最後に取れた位置' : '現在地');
    // 精度の円（半径＝精度m）。精度が悪い（50m超）なら同じ円が大きくなるだけ。取れていないときは円なし
    if (lost) {
      meCircle?.remove();
      meCircle = null;
    } else if (!meCircle) {
      meCircle = L.circle(ll, { radius: fix.acc, color: INK, opacity: 0.45, weight: 1.5, fillColor: INK, fillOpacity: 0.13, interactive: false }).addTo(map);
    } else {
      meCircle.setLatLng(ll);
      meCircle.setRadius(fix.acc);
    }
  }

  function renderQueens(s: GameState): void {
    const list = queensOf(s);
    const sig = list.map((q) => `${q.key}|${q.latest}|${hiddenLabels.has(q.key)}`).join(',');
    if (sig === queensSig) return;
    queensSig = sig;
    queenLayer.clearLayers();
    queens.clear();
    for (const q of list) addQueen(q);
    tickQueenAgo();
  }

  function addQueen(q: QueenView): void {
    const root = h('div', 'mp-queen');
    const bee = queenSvg();
    bee.classList.add('mp-qb');
    root.append(bee);
    let label: HTMLElement | null = null;
    let ago: HTMLElement | null = null;
    if (q.latest) {
      ago = h('b', 'mp-num', '');
      label = h('span', 'mp-ql', h('b', undefined, q.teamName), ' 女王の飛行', h('small', undefined, '最終クリア時刻：', ago, '分前'));
      label.setAttribute('role', 'button');
      if (hiddenLabels.has(q.key)) label.classList.add('mp-off');
      L.DomEvent.disableClickPropagation(label);
      label.addEventListener('click', () => {
        hiddenLabels.add(q.key);
        label?.classList.add('mp-off');
        queensSig = '';
      });
      root.append(label);
    }
    root.setAttribute('role', 'img');
    root.setAttribute('aria-label', `${q.teamName} 女王の飛行`);
    const marker = L.marker([q.lat, q.lon], { icon: L.divIcon({ html: root, className: 'mp-queen-icon', iconSize: [0, 0] }), keyboard: false, zIndexOffset: 500, interactive: !!label });
    marker.addTo(queenLayer);
    queens.set(q.key, { marker, root, label, ago, at: q.at });
  }

  function tickQueenAgo(): void {
    const now = serverNow();
    for (const q of queens.values()) {
      if (!q.ago) continue;
      const min = q.at ? Math.max(1, Math.floor((now - Date.parse(q.at)) / 60000)) : 1;
      q.ago.textContent = String(min);
    }
  }

  function renderScouts(s: GameState): void {
    const circles = scoutCircles(s.scouts, serverNow());
    const sig = circles.map((c) => `${c.effectId}|${c.teamId}|${c.lat}|${c.lon}|${c.radiusM}`).join(',');
    if (sig === scoutsSig) return;
    scoutsSig = sig;
    scoutLayer.clearLayers();
    for (const c of circles) {
      L.circle([c.lat, c.lon], { radius: c.radiusM, color: INK, weight: 2, opacity: 0.85, dashArray: '6 5', fill: true, fillColor: INK, fillOpacity: 0.05, className: 'mp-scout', interactive: false }).addTo(scoutLayer);
      const lab = h('div', 'mp-scout-label', h('span', undefined, c.teamName));
      L.marker([c.lat, c.lon], { icon: L.divIcon({ html: lab, className: 'mp-scout-icon', iconSize: [0, 0] }), interactive: false, keyboard: false, zIndexOffset: -100 }).addTo(scoutLayer);
    }
    if (scoutFocusPending) applyScoutFocus();
  }

  function applyScoutFocus(): void {
    if (!map || !state) return;
    const circles = scoutCircles(state.scouts, serverNow());
    if (!circles.length) return;
    let b: L.LatLngBounds | null = null;
    for (const c of circles) {
      const cb = L.circle([c.lat, c.lon], { radius: c.radiusM }).getBounds();
      b = b ? b.extend(cb) : cb;
    }
    if (b) map.fitBounds(b, { paddingTopLeft: [30, head.offsetHeight + 40], paddingBottomRight: [30, 30], maxZoom: 18 });
    scoutFocusPending = false;
  }

  function fitAll(s: GameState): void {
    if (!map) return;
    const pts = s.spots.map((p) => L.latLng(p.latitude, p.longitude));
    if (!pts.length) return;
    const b = L.latLngBounds(pts);
    map.fitBounds(b, { paddingTopLeft: [30, head.offsetHeight + 40], paddingBottomRight: [30, 40], maxZoom: 17, animate: false });
    fitted = true;
  }

  function fitArea(areaId: string): void {
    if (!map || !state) return;
    const pts = state.spots.filter((p) => p.area_id === areaId).map((p) => L.latLng(p.latitude, p.longitude));
    if (!pts.length) return;
    if (pts.length === 1 && pts[0]) map.setView(pts[0], 18);
    else map.fitBounds(L.latLngBounds(pts), { paddingTopLeft: [40, head.offsetHeight + 50], paddingBottomRight: [40, 50], maxZoom: 18 });
  }

  function renderMapLayers(): void {
    if (!map || !state) return;
    const items = buildStrip(state, serverNow());
    renderAreas(state, items);
    renderPins(state, items);
    renderQueens(state);
    renderScouts(state);
    renderMe();
    if (!fitted) fitAll(state);
  }

  // ------------------------------------------------------------ 倍率の帯と詳細
  function renderStrip(s: GameState): void {
    const items = buildStrip(s, serverNow());
    const sig = JSON.stringify(items.map((i) => [i.areaId, i.name, i.color, i.multText, !!i.honey, i.honey?.mult, !!i.hornet]));
    if (sig !== stripSig) {
      stripSig = sig;
      stripEl.style.gridTemplateColumns = `repeat(${Math.max(1, items.length)}, minmax(0, 1fr))`;
      stripEl.replaceChildren(
        ...items.map((it) => {
          const b = h('button');
          b.type = 'button';
          b.style.setProperty('--c', it.color);
          b.dataset.area = it.areaId;
          b.setAttribute('aria-expanded', String(openAreaId === it.areaId));
          const row = h('span', 'mp-row', multEl(it.multText));
          if (it.hornet) row.append(h('span', 'mp-hb mp-hz', '襲来'));
          if (it.honey) row.append(h('span', 'mp-hb', '蜜源', multEl(formatMult(it.honey.mult))));
          b.append(h('span', 'mp-nm', it.name), row);
          b.addEventListener('click', () => toggleDetail(it.areaId));
          return b;
        }),
      );
    }
    if (openAreaId) {
      if (items.some((i) => i.areaId === openAreaId)) fillDetail(openAreaId);
      else closeDetail();
    }
    renderDuel(s);
    layout();
  }

  function layout(): void {
    const bottom = head.offsetTop + head.offsetHeight;
    slip.style.top = `${bottom + 8}px`;
    detail.style.top = `${bottom + 6}px`;
  }

  function remainText(endsAt: string | null): string {
    return endsAt ? fmtRemaining(remainingMs(endsAt)) : '';
  }

  function toggleDetail(areaId: string): void {
    if (openAreaId === areaId) return closeDetail();
    openAreaId = areaId;
    closeSpot('close');
    fillDetail(areaId);
    detail.hidden = false;
    for (const b of stripEl.querySelectorAll('button')) b.setAttribute('aria-expanded', String(b.dataset.area === areaId));
  }

  function closeDetail(): void {
    if (openAreaId === null && detail.hidden) return;
    openAreaId = null;
    detail.hidden = true;
    for (const b of stripEl.querySelectorAll('button')) b.setAttribute('aria-expanded', 'false');
  }

  function fillDetail(areaId: string): void {
    if (!state) return;
    const it = buildStrip(state, serverNow()).find((i) => i.areaId === areaId);
    if (!it) return;
    detail.style.setProperty('--c', it.color);
    const dl = h('dl');
    dl.append(h('dt', undefined, 'クリア'), h('dd', undefined, h('b', 'mp-num', String(it.clears)), ' 回'));
    dl.append(h('dt', undefined, '次のクリアで'), h('dd', undefined, it.next.kind === 'cap' ? '上限です' : h('span', undefined, multEl(it.next.text, 'mp-num'))));
    dl.append(h('dt', undefined, 'スポット'), h('dd', undefined, '残り ', h('b', 'mp-num', String(it.spotsLeft)), ` ／ ${it.spotsTotal}`));
    if (it.honey) {
      const left = h('b', 'mp-num mp-hl', remainText(it.honey.endsAt));
      left.dataset.until = it.honey.endsAt ?? '';
      dl.append(h('div', 'mp-hrow', h('span', undefined, `蜜源　全チームの得点 ×${formatMult(it.honey.mult)}`), it.honey.endsAt ? h('span', undefined, '残り ', left) : null));
    }
    if (it.hornet) {
      const left = h('b', 'mp-num mp-hl', remainText(it.hornet.endsAt));
      left.dataset.until = it.hornet.endsAt ?? '';
      const title = h('span', undefined, `スズメバチの襲来${it.hornet.by ? `（${it.hornet.by}）` : ''}`, h('small', undefined, 'スポットのクリア不可。お題のクリアは可'));
      dl.append(h('div', 'mp-hrow mp-hz', title, it.hornet.endsAt ? h('span', undefined, '残り ', left) : null));
    }
    const go = h('button', 'mp-go', '地図でこのエリアを見る');
    go.type = 'button';
    go.addEventListener('click', () => {
      fitArea(areaId);
      closeDetail();
    });
    const title = h('h3', undefined, h('span', undefined, it.name), multEl(it.multText));
    detail.replaceChildren(title, dl, go);
  }

  // ------------------------------------------------------------ 縄張り争いの帯
  function renderDuel(s: GameState): void {
    const d = duelOf(s);
    if (!d) {
      duelEl.hidden = true;
      return;
    }
    const left = h('b', 'mp-num mp-dleft', d.endsAt ? fmtRemaining(remainingMs(d.endsAt)) : '');
    left.dataset.until = d.endsAt ?? '';
    duelEl.replaceChildren(
      h('span', 'mp-dt', '縄張り争い', h('small', undefined, duelRule(d.side, d.points))),
      h('span', 'mp-ds', h('span', undefined, '自チーム'), h('b', 'mp-num', String(d.mine)), h('i', undefined, '－'), h('b', 'mp-num', String(d.theirs)), h('span', 'mp-who', d.opponent)),
      h('span', 'mp-dl', '残り ', left),
    );
    duelEl.hidden = false;
  }

  // ------------------------------------------------------------ スポットの詳細（㉑）
  function openSpot(spotId: number): void {
    if (!state) return;
    const sp = state.spots.find((x) => x.spot_id === spotId);
    if (!sp) return;
    closeDetail();
    if (selectedSpot !== null && selectedSpot !== spotId) closeSpot('close');
    if (selectedSpot !== spotId) {
      selectedSpot = spotId;
      // 開いた時点で閲覧ログ（opened_from='map'）。閉じたら返り値の関数で閉じる
      spotView = openView('spot', spotId, 'map');
    }
    renderPins(state, buildStrip(state, serverNow()));
    fillSpot(sp);
    spotEl.classList.remove('mp-gone');
    spotEl.classList.add('mp-open');
    requestAnimationFrame(() => keepPinVisible(sp));
  }

  function keepPinVisible(sp: StateSpot): void {
    if (!map) return;
    const mapTop = sheet.offsetTop;
    const p = map.latLngToContainerPoint([sp.latitude, sp.longitude]);
    const topLimit = head.offsetTop + head.offsetHeight + 24 - mapTop;
    const bottomLimit = spotEl.offsetTop - mapTop - 30;
    if (bottomLimit <= topLimit) return;
    if (p.y > bottomLimit || p.y < topLimit) {
      const target = topLimit + (bottomLimit - topLimit) / 2;
      map.panBy([0, p.y - target], { animate: true });
    }
  }

  function fillSpot(sp: StateSpot): void {
    if (!state) return;
    const idx = spotNumber(state.spots, sp.spot_id);
    const items = buildStrip(state, serverNow());
    const it = items.find((i) => i.areaId === sp.area_id);
    const area = state.areas.find((a) => a.area_id === sp.area_id);
    spotEl.style.setProperty('--c', it?.color ?? INK);
    const close = h('button', 'mp-sp-close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', '閉じる');
    close.addEventListener('click', () => closeSpot('close'));
    const dl = h('dl', 'mp-sp-dl');
    dl.append(h('dt', undefined, '基礎点'), h('dd', undefined, h('b', 'mp-num', String(sp.base_point)), ' 点'));
    const multCell = h('dd', undefined, multEl(it?.multText ?? formatMult(area?.multiplier ?? 1), 'mp-num'));
    if (it?.honey) multCell.append(' ', h('small', undefined, `蜜源 ×${formatMult(it.honey.mult)}`));
    dl.append(h('dt', undefined, 'いまの倍率'), multCell);
    const bs = spotButton(sp, it?.hornet ?? null);
    const btn = h('button', `mp-sp-btn${bs.kind === 'blocked' ? ' mp-blocked' : ''}`, bs.label);
    btn.type = 'button';
    btn.setAttribute('aria-disabled', String(bs.disabled));
    let busy = false;
    btn.addEventListener('click', () => {
      if (bs.disabled || busy) return;
      busy = true;
      void submitFrom(sp).finally(() => {
        busy = false;
      });
    });
    spotEl.replaceChildren(
      close,
      h('div', 'mp-sp-head', h('span', 'mp-num', String(idx)), h('span', undefined, area?.name ?? '')),
      h('h3', undefined, sp.name),
      ...(sp.description ? [h('p', 'mp-sp-desc', sp.description)] : []),
      dl,
      btn,
    );
  }

  async function submitFrom(sp: StateSpot): Promise<void> {
    const closeView = spotView ? await spotView : undefined;
    const out = await ctx.submitPhoto(el, { kind: 'spot', targetId: sp.spot_id, title: sp.name, areaId: sp.area_id, closeView });
    if (out.status === 'sent' || out.status === 'queued') {
      closeSpot('submitted');
      void ctx.refresh();
    }
  }

  function closeSpot(reason: string): void {
    if (selectedSpot === null) return;
    const id = selectedSpot;
    const v = spotView;
    selectedSpot = null;
    spotView = null;
    spotEl.classList.remove('mp-open');
    if (v) void v.then((close) => close(reason));
    if (state && map) renderPins(state, buildStrip(state, serverNow()));
    else pins.get(id)?.marker.getElement()?.classList.remove('mp-sel');
  }

  // ------------------------------------------------------------ 1秒ごとの更新（表示中だけ）
  function tick(): void {
    const now = Date.now();
    const lost = isGpsLost(geo.status);
    const visible = slipSchedule.update(lost, now);
    slip.classList.toggle('mp-on', visible);
    if (visible) slipAgo.textContent = gpsAgoText(geo.lastFix?.at, now);
    for (const e of el.querySelectorAll<HTMLElement>('[data-until]')) {
      const until = e.dataset.until;
      if (until) e.textContent = fmtRemaining(remainingMs(until));
    }
    tickQueenAgo();
  }

  function startTimer(): void {
    if (timer) return;
    timer = setInterval(tick, 1000);
  }

  function stopTimer(): void {
    if (timer) clearInterval(timer);
    timer = null;
  }

  geo.changed.on(() => {
    if (shown && map && !folding) renderMe();
  });

  // ------------------------------------------------------------ 開く演出（㉒）
  /**
   * 地図コンテナ（タイル・縁・印を含む DOM）を4回複製し、各面に幅1/4ずつ見えるよう切り出して屏風折りにする。
   * 画面の中心から左右に 0.55 秒（ease-out の3乗）で開く。開き始めは各面 80°、光に向いた面を明るく反対を暗く。
   * 動くのは地図の紙だけ（帯・縄張り争いの帯・上部の帯・下部タブは動かさない）。開いている間は印を隠す。
   */
  async function playFold(): Promise<void> {
    if (!map || typeof Element.prototype.animate !== 'function') return;
    const w = face.clientWidth;
    const hgt = face.clientHeight;
    if (w < 40 || hgt < 40) return;
    const pw = w / 4;
    const stage = h('div', 'mp-fold');
    stage.setAttribute('aria-hidden', 'true');
    const make = (i: number, dark: boolean): { p: HTMLElement; shade: HTMLElement } => {
      const p = h('div', 'mp-fold-p');
      p.style.width = `${pw}px`;
      const v = h('div', 'mp-fold-v');
      const clone = mapDiv.cloneNode(true) as HTMLElement;
      clone.removeAttribute('id');
      clone.classList.add('mp-clone');
      Object.assign(clone.style, { position: 'absolute', left: `${-i * pw}px`, top: '0px', right: 'auto', bottom: 'auto', width: `${w}px`, height: `${hgt}px` });
      clone.querySelectorAll('[tabindex]').forEach((n) => n.removeAttribute('tabindex'));
      const shade = h('div', 'mp-fold-s');
      shade.style.background = dark ? 'rgba(30,20,10,.45)' : 'rgba(255,255,255,.3)';
      v.append(clone, shade);
      p.append(v);
      return { p, shade };
    };
    const p0 = make(0, true);
    const p1 = make(1, false);
    const p2 = make(2, true);
    const p3 = make(3, false);
    const place = (n: { p: HTMLElement }, left: string, origin: string): void => {
      n.p.style.left = left;
      n.p.style.transformOrigin = origin;
    };
    place(p1, `${pw}px`, 'right center');
    place(p2, `${2 * pw}px`, 'left center');
    place(p0, '-100%', 'right center');
    place(p3, '100%', 'left center');
    p1.p.append(p0.p);
    p2.p.append(p3.p);
    stage.append(p1.p, p2.p);
    sheet.append(stage);
    folding = true;
    el.classList.add('mp-folding');
    const opts: KeyframeAnimationOptions = { duration: FOLD_MS, easing: 'cubic-bezier(0.33, 1, 0.68, 1)', fill: 'both' };
    const rot = (n: { p: HTMLElement }, deg: number): Animation => n.p.animate([{ transform: `rotateY(${deg}deg)` }, { transform: 'rotateY(0deg)' }], opts);
    const anims = [rot(p1, -FOLD_ANGLE), rot(p0, 2 * FOLD_ANGLE), rot(p2, FOLD_ANGLE), rot(p3, -2 * FOLD_ANGLE)];
    for (const n of [p0, p1, p2, p3]) anims.push(n.shade.animate([{ opacity: 1 }, { opacity: 0 }], opts));
    try {
      await Promise.all(anims.map((a) => a.finished));
    } catch {
      /* 中断されても片付ける */
    } finally {
      stage.remove();
      el.classList.remove('mp-folding');
      folding = false;
    }
  }

  // ------------------------------------------------------------ Tab
  function applyFocus(f: TabShowOptions['focus'] | undefined): void {
    if (!f || !map || !state) return;
    if (f.scout) {
      scoutFocusPending = true;
      applyScoutFocus();
    }
    if (f.areaId) fitArea(f.areaId);
    if (f.spotId !== undefined) {
      const sp = state.spots.find((x) => x.spot_id === f.spotId);
      if (sp) {
        map.setView([sp.latitude, sp.longitude], Math.max(map.getZoom(), 17), { animate: false });
        openSpot(sp.spot_id);
      }
    }
  }

  function sizeFace(): void {
    if (face.offsetWidth > 0) face.style.clipPath = deckle(face.offsetWidth, face.offsetHeight, 4271);
  }

  function show(opts: TabShowOptions): Promise<void> | void {
    shown = true;
    const m = ensureMap();
    // 戻ってきたら女王の札はまた出る
    if (hiddenLabels.size) {
      hiddenLabels.clear();
      queensSig = '';
    }
    m.invalidateSize({ animate: false });
    sizeFace();
    layout();
    if (state) renderStrip(state);
    renderMapLayers();
    if (opts.focus) pendingFocus = opts.focus;
    const f = pendingFocus;
    pendingFocus = null;
    if (f?.scout && !(state && scoutCircles(state.scouts, serverNow()).length)) {
      scoutFocusPending = true;
    }
    applyFocus(f ?? undefined);
    startTimer();
    tick();
    const first = !hadFirstShow;
    hadFirstShow = true;
    if (!opts.animate || prefersReducedMotion()) return;
    return (async () => {
      // 最初の1回は、タイルが読み込まれるのを少し待ってから複製する（複製した絵に地図が写るように）
      if (first && tiles) {
        const t = tiles;
        await Promise.race([new Promise<void>((r) => t.once('load', () => r())), sleep(600)]);
      }
      await playFold();
    })();
  }

  function hide(): void {
    shown = false;
    stopTimer();
    closeDetail();
    closeSpot('tab_switch');
    slip.classList.remove('mp-on');
  }

  // 帯の高さが変わったとき（フォント読み込み後など）に札の位置を直す
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(() => {
      if (shown) {
        layout();
        sizeFace();
      }
    }).observe(head);
  }

  // ------------------------------------------------------------ 通知（どの画面にいても出すもの）
  // 札の使用・縄張り争い・偵察の知らせはカードタブが出す（51）。地図は女王が止められた知らせだけ
  function notifyChanges(s: GameState, prev: GameState): void {
    const out: Notice[] = [];
    const hadTake = new Set(prev.recent_queen_takes.map((t) => t.effect_id));
    for (const t of s.recent_queen_takes) {
      if (hadTake.has(t.effect_id)) continue;
      out.push({ heading: '女王の飛行', body: `${t.taken_by_name}が${t.queen_team_name}の女王を止めました`, at: t.resolved_at });
    }
    for (const n of out) ctx.notify(n);
  }

  function update(s: GameState, prev: GameState | null): void {
    state = s;
    if (prev) notifyChanges(s, prev);
    // 帯と詳細は小さいので隠れていても更新する。地図のレイヤーは表示中だけ
    renderStrip(s);
    if (openAreaId === null && selectedSpot !== null) {
      const sp = s.spots.find((x) => x.spot_id === selectedSpot);
      if (sp) fillSpot(sp);
    }
    if (shown && map) renderMapLayers();
  }

  return { id: 'map', el, show, hide, update };
};
